import React, { useCallback, useRef, useState } from 'react'
import { format as formatCpf } from '@fnando/cpf'
import {
  Alert,
  Button,
  Checkbox,
  Progress,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Upload,
} from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { apiUrl } from '../../index'
import api from '../../api'
import { CursoModel } from '../../models/CursoModel'

const { TabPane } = Tabs
const { Option } = Select
const { Dragger } = Upload

type WizardStep = 'input' | 'mapping' | 'validating' | 'validation' | 'results'

type ColumnMapping = Record<string, string>

type ParsedAluno = {
  cpf: string
  nome: string
  dataNascimento?: string
  cnh?: string
  categoriaCnh?: string
  email?: string
  telefone?: string
  uf?: string
  municipio?: string
  cursoId?: number
  [key: string]: string | number | undefined
}

type ValidationRow = {
  cpf: string
  parsed: ParsedAluno
  status: 'valid' | 'invalid'
  errors: string[]
  checked: boolean
}

type ImportResult = {
  cpf: string
  nome: string
  success: boolean
  error?: string
}

type ImportProgress = {
  processados: number
  total: number
  resultados: ImportResult[]
}

type ValidationProgress = {
  processados: number
  total: number
}

const SYSTEM_FIELDS = [
  'nome',
  'cpf',
  'dataNascimento',
  'cnh',
  'categoriaCnh',
  'email',
  'telefone',
  'uf',
  'municipio',
  'curso',
]
const REQUIRED_SYSTEM_FIELDS = ['nome', 'cpf']

function parseCsvText(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0)
  if (lines.length === 0) return { headers: [], rows: [] }

  const parseLine = (line: string): string[] => {
    const result: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = !inQuotes
        }
      } else if ((char === ',' || char === '\t') && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    result.push(current.trim())
    return result
  }

  const headers = parseLine(lines[0])
  const rows = lines.slice(1).map(parseLine)
  return { headers, rows }
}

function parseDateBR(raw: string): string | null {
  if (!raw) return null
  const normalised = raw.replace(/-/g, '/')
  const parts = normalised.split('/')
  if (parts.length !== 3) return null

  const [day, month, year] = parts
  if (!day || !month || !year || year.length !== 4) return null

  const dayPadded = day.padStart(2, '0')
  const monthPadded = month.padStart(2, '0')

  const date = new Date(`${year}-${monthPadded}-${dayPadded}`)
  if (isNaN(date.getTime())) return null

  return `${year}-${monthPadded}-${dayPadded}`
}

function applyMappingToRow(
  headers: string[],
  row: string[],
  mapping: ColumnMapping
): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [systemField, csvColumn] of Object.entries(mapping)) {
    if (!csvColumn) continue
    const columnIndex = headers.indexOf(csvColumn)
    if (columnIndex >= 0) {
      result[systemField] = row[columnIndex] || ''
    }
  }
  return result
}

function parseAlunoEstrutura(
  rawFields: Record<string, string>,
  cursos: CursoModel[]
): { parsed: ParsedAluno; localErrors: string[] } {
  const localErrors: string[] = []

  const digits = (rawFields.cpf || '').replace(/\D/g, '')
  const cpf = formatCpf(digits) || digits

  let dataNascimento: string | undefined
  if (rawFields.dataNascimento) {
    const parsed = parseDateBR(rawFields.dataNascimento)
    if (!parsed) {
      localErrors.push('Data de nascimento inválida (use DD/MM/YYYY)')
    } else {
      dataNascimento = parsed
    }
  }

  let cursoId: number | undefined
  if (rawFields.curso) {
    const cursoEncontrado = cursos.find(
      (c) => c.nomeReduzido?.toLowerCase() === rawFields.curso?.toLowerCase()
    )
    if (!cursoEncontrado) {
      localErrors.push('Curso não reconhecido')
    } else {
      cursoId = cursoEncontrado.id
    }
  }

  const parsed: ParsedAluno = {
    cpf,
    nome: rawFields.nome || '',
    dataNascimento,
    cnh: rawFields.cnh,
    categoriaCnh: rawFields.categoriaCnh,
    email: rawFields.email,
    telefone: rawFields.telefone,
    uf: rawFields.uf,
    municipio: rawFields.municipio,
    cursoId,
  }

  return { parsed, localErrors }
}

async function validarAlunoNoBackend(parsed: ParsedAluno): Promise<string[]> {
  try {
    const result = await api.Matriculas.validar({
      cpf: parsed.cpf,
      nome: parsed.nome,
      dataNascimento: parsed.dataNascimento,
      cnh: parsed.cnh,
      categoriaCnh: parsed.categoriaCnh,
      email: parsed.email,
      telefone: parsed.telefone,
      uf: parsed.uf,
      municipio: parsed.municipio,
      cursoId: parsed.cursoId,
    })
    if (result && !result.success) return [result.errorMessage || 'Erro de validação']
    return []
  } catch (err: any) {
    return [err.message || 'Erro de validação']
  }
}

export function ImportarCsvTurma({
  turmaId,
  onImportComplete,
}: {
  turmaId: number
  onImportComplete: () => void
}) {
  const [step, setStep] = useState<WizardStep>('input')
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [csvRows, setCsvRows] = useState<string[][]>([])
  const [columnMapping, setColumnMapping] = useState<ColumnMapping | null>(null)
  const [validationRows, setValidationRows] = useState<ValidationRow[]>([])
  const validationRowsRef = useRef<ValidationRow[]>([])
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null)
  const [validationProgress, setValidationProgress] = useState<ValidationProgress | null>(null)
  const [cursos, setCursos] = useState<CursoModel[]>([])
  const [isImporting, setIsImporting] = useState(false)
  const [loadingCursos, setLoadingCursos] = useState(false)
  const [erroGlobal, setErroGlobal] = useState<string | null>(null)

  validationRowsRef.current = validationRows

  const loadCursos = useCallback(async (): Promise<CursoModel[]> => {
    if (cursos.length > 0) return cursos
    setLoadingCursos(true)
    try {
      const resultado = await api.Cursos.getAll()
      setCursos(resultado)
      return resultado
    } catch {
      return []
    } finally {
      setLoadingCursos(false)
    }
  }, [cursos])

  const handleCsvParsed = useCallback(
    async (headers: string[], rows: string[][]) => {
      setCsvHeaders(headers)
      setCsvRows(rows)

      const cursosCarregados = await loadCursos()

      if (columnMapping) {
        setStep('validating')
        await runValidation(headers, rows, columnMapping, cursosCarregados)
        setStep('validation')
      } else {
        setStep('mapping')
      }
    },
    [columnMapping, loadCursos]
  )

  const handleMappingConfirmed = useCallback(
    async (mapping: ColumnMapping) => {
      setColumnMapping(mapping)
      setStep('validating')
      await runValidation(csvHeaders, csvRows, mapping, cursos)
      setStep('validation')
    },
    [csvHeaders, csvRows, cursos]
  )

  const runValidation = useCallback(
    async (headers: string[], rows: string[][], mapping: ColumnMapping, cursosAtuais: CursoModel[]) => {
      const rawRows = rows.map((row) => applyMappingToRow(headers, row, mapping))
      const parsedRows = rawRows.map((rawFields) => parseAlunoEstrutura(rawFields, cursosAtuais))

      const previousMap = new Map(validationRowsRef.current.map((row) => [row.cpf, row]))

      setValidationProgress({ processados: 0, total: parsedRows.length })
      const backendErrors = await Promise.all(
        parsedRows.map(async ({ parsed, localErrors }) => {
          const skipBackend =
            previousMap.get(parsed.cpf)?.status === 'valid' || localErrors.length > 0
          const errors = skipBackend ? null : await validarAlunoNoBackend(parsed)
          setValidationProgress((prev) => prev && { ...prev, processados: prev.processados + 1 })
          return errors
        })
      )

      const newRows: ValidationRow[] = parsedRows.map(({ parsed, localErrors }, index) => {
        const previous = previousMap.get(parsed.cpf)
        if (previous?.status === 'valid') return { ...previous, parsed }

        const errors = localErrors.length > 0 ? localErrors : (backendErrors[index] ?? [])
        const isValid = errors.length === 0
        return {
          cpf: parsed.cpf,
          parsed,
          status: isValid ? 'valid' : 'invalid',
          errors,
          checked: isValid,
        }
      })

      setValidationRows(newRows)
    },
    []
  )

  const handleImport = useCallback(async () => {
    const alunosParaImportar = validationRows
      .filter((row) => row.status === 'valid' && row.checked)
      .map((row) => ({
        cpf: row.parsed.cpf,
        nome: row.parsed.nome,
        email: row.parsed.email || '',
        telefone: row.parsed.telefone || '',
        dataNascimento: row.parsed.dataNascimento,
        cnh: row.parsed.cnh,
        categoriaCnh: row.parsed.categoriaCnh,
        uf: row.parsed.uf || '',
        municipio: row.parsed.municipio,
        cursoId: row.parsed.cursoId,
        ufDestino: row.parsed.uf || '',
        ufParceiro: row.parsed.uf || '',
        comboVsoft: false,
      }))

    const nomeByClf = Object.fromEntries(validationRows.map((r) => [r.parsed.cpf, r.parsed.nome]))

    const total = alunosParaImportar.length
    setImportProgress({ processados: 0, total, resultados: [] })
    setIsImporting(true)
    setStep('results')

    try {
      const response = await fetch(`${apiUrl}/turmas/${turmaId}/importar-alunos`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', platform: 'PAINEL' },
        body: JSON.stringify({ alunos: alunosParaImportar }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        setErroGlobal(data.message ?? `Erro ${response.status}`)
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const evento = JSON.parse(line.slice(6))

          if (evento.tipo === 'progresso') {
            const result: ImportResult = {
              cpf: evento.cpf,
              nome: nomeByClf[evento.cpf] || '',
              success: evento.success,
              error: evento.error,
            }
            setImportProgress((prev) => ({
              processados: evento.processados,
              total: evento.total,
              resultados: [...(prev?.resultados ?? []), result],
            }))
          }

          if (evento.tipo === 'concluido') {
            onImportComplete()
          }

          if (evento.tipo === 'erro') {
            setErroGlobal(evento.message)
          }
        }
      }
    } catch {
      // stream ended or network error; results already shown
    } finally {
      setIsImporting(false)
    }
  }, [validationRows, turmaId, onImportComplete])

  const toggleRowChecked = useCallback((cpf: string) => {
    setValidationRows((prev) =>
      prev.map((row) => (row.cpf === cpf ? { ...row, checked: !row.checked } : row))
    )
  }, [])

  const toggleAllValidChecked = useCallback((checked: boolean) => {
    setValidationRows((prev) =>
      prev.map((row) => (row.status === 'valid' ? { ...row, checked } : row))
    )
  }, [])

  const validCheckedCount = validationRows.filter((r) => r.status === 'valid' && r.checked).length
  const allValidChecked =
    validationRows.filter((r) => r.status === 'valid').length > 0 &&
    validationRows.filter((r) => r.status === 'valid').every((r) => r.checked)

  return (
    <div>
      {step === 'input' && (
        <InputStep onCsvParsed={handleCsvParsed} loadingCursos={loadingCursos} />
      )}
      {step === 'mapping' && (
        <MappingStep
          headers={csvHeaders}
          onMappingConfirmed={handleMappingConfirmed}
          onBack={() => setStep('input')}
        />
      )}
      {step === 'validating' && <ValidatingStep progress={validationProgress} />}
      {step === 'validation' && (
        <ValidationStep
          rows={validationRows}
          allValidChecked={allValidChecked}
          validCheckedCount={validCheckedCount}
          isImporting={isImporting}
          onToggleRow={toggleRowChecked}
          onToggleAll={toggleAllValidChecked}
          onImport={handleImport}
          onChangeMapping={() => setStep('mapping')}
          onReupload={() => setStep('input')}
        />
      )}
      {step === 'results' && (
        <ResultsStep
          progress={importProgress}
          isImporting={isImporting}
          erroGlobal={erroGlobal}
          onClose={onImportComplete}
        />
      )}
    </div>
  )
}

function InputStep({
  onCsvParsed,
  loadingCursos,
}: {
  onCsvParsed: (headers: string[], rows: string[][]) => void
  loadingCursos: boolean
}) {
  const [pastedText, setPastedText] = useState('')

  const parseAndAdvance = useCallback(
    (text: string) => {
      const { headers, rows } = parseCsvText(text)
      if (headers.length === 0) return
      onCsvParsed(headers, rows)
    },
    [onCsvParsed]
  )

  const handleFileUpload = useCallback(
    (file: File) => {
      const reader = new FileReader()
      reader.onload = (event) => {
        parseAndAdvance(event.target?.result as string)
      }
      reader.readAsText(file, 'UTF-8')
      return false
    },
    [parseAndAdvance]
  )

  return (
    <div>
      {loadingCursos && <Spin tip="Carregando cursos..." style={{ marginBottom: 16 }} />}
      <Tabs defaultActiveKey="upload">
        <TabPane tab="Upload de arquivo" key="upload">
          <Dragger
            accept=".csv"
            beforeUpload={handleFileUpload}
            showUploadList={false}
            style={{ padding: 20 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p>Arraste um arquivo CSV ou clique para selecionar</p>
          </Dragger>
        </TabPane>
        <TabPane tab="Colar texto" key="paste">
          <textarea
            style={{ width: '100%', height: 200, fontFamily: 'monospace', fontSize: 13 }}
            placeholder="Cole o conteúdo CSV aqui..."
            value={pastedText}
            onChange={(event) => setPastedText(event.target.value)}
          />
          <Button
            type="primary"
            style={{ marginTop: 8 }}
            onClick={() => parseAndAdvance(pastedText)}
            disabled={!pastedText.trim()}
          >
            Confirmar
          </Button>
        </TabPane>
      </Tabs>
    </div>
  )
}

function normalizeForMatching(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

function autoMap(headers: string[]): ColumnMapping {
  const usedHeaders = new Set<string>()
  const mapping: ColumnMapping = {}

  for (const field of SYSTEM_FIELDS) {
    const normalizedField = normalizeForMatching(field)
    const match = headers.find(
      (h) => normalizeForMatching(h) === normalizedField && !usedHeaders.has(h)
    )
    mapping[field] = match ?? ''
    if (match) usedHeaders.add(match)
  }

  return mapping
}

function MappingStep({
  headers,
  onMappingConfirmed,
  onBack,
}: {
  headers: string[]
  onMappingConfirmed: (mapping: ColumnMapping) => void
  onBack: () => void
}) {
  const [mapping, setMapping] = useState<ColumnMapping>(() => autoMap(headers))

  const usedColumns = new Set(Object.values(mapping).filter(Boolean))

  const availableFor = (field: string) =>
    headers.filter((h) => !usedColumns.has(h) || h === mapping[field])

  const updateMapping = (field: string, value: string) => {
    setMapping((prev) => ({ ...prev, [field]: value }))
  }

  const requiredMapped = REQUIRED_SYSTEM_FIELDS.every((field) => mapping[field])

  return (
    <div>
      <p>Associe as colunas do CSV aos campos do sistema:</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee' }}>
              Campo do sistema
            </th>
            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee' }}>
              Coluna do CSV
            </th>
          </tr>
        </thead>
        <tbody>
          {SYSTEM_FIELDS.map((field) => (
            <tr key={field}>
              <td style={{ padding: '6px 8px' }}>
                {field}
                {REQUIRED_SYSTEM_FIELDS.includes(field) && (
                  <span style={{ color: 'red', marginLeft: 4 }}>*</span>
                )}
              </td>
              <td style={{ padding: '6px 8px' }}>
                <Select
                  style={{ width: '100%' }}
                  value={mapping[field] || undefined}
                  onChange={(value) => updateMapping(field, value || '')}
                  allowClear
                  placeholder="— não mapear —"
                >
                  {availableFor(field).map((header) => (
                    <Option key={header} value={header}>
                      {header}
                    </Option>
                  ))}
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Space>
        <Button onClick={onBack}>Voltar</Button>
        <Button
          type="primary"
          disabled={!requiredMapped}
          onClick={() => onMappingConfirmed(mapping)}
        >
          Confirmar mapeamento
        </Button>
      </Space>
    </div>
  )
}

function ValidatingStep({ progress }: { progress: ValidationProgress | null }) {
  const total = progress?.total ?? 0
  const processados = progress?.processados ?? 0
  const pct = total > 0 ? Math.round((processados / total) * 100) : 0

  return (
    <div>
      <Progress percent={pct} status="active" style={{ marginBottom: 8 }} />
      <p>
        Validando {processados} de {total} alunos...
      </p>
    </div>
  )
}

function ValidationStep({
  rows,
  allValidChecked,
  validCheckedCount,
  isImporting,
  onToggleRow,
  onToggleAll,
  onImport,
  onChangeMapping,
  onReupload,
}: {
  rows: ValidationRow[]
  allValidChecked: boolean
  validCheckedCount: number
  isImporting: boolean
  onToggleRow: (cpf: string) => void
  onToggleAll: (checked: boolean) => void
  onImport: () => void
  onChangeMapping: () => void
  onReupload: () => void
}) {
  const columns: ColumnsType<ValidationRow> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Nome',
      dataIndex: ['parsed', 'nome'],
      width: 180,
    },
    {
      title: 'CPF',
      dataIndex: ['parsed', 'cpf'],
      width: 130,
    },
    {
      title: 'UF',
      dataIndex: ['parsed', 'uf'],
      width: 60,
    },
    {
      title: 'Município',
      dataIndex: ['parsed', 'municipio'],
      width: 140,
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, row) =>
        row.status === 'valid' ? <Tag color="green">VÁLIDO</Tag> : <Tag color="red">INVÁLIDO</Tag>,
    },
    {
      title: 'Erros',
      key: 'errors',
      render: (_, row) =>
        row.errors.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {row.errors.map((error, index) => (
              <li key={index} style={{ color: 'red', fontSize: 12 }}>
                {error}
              </li>
            ))}
          </ul>
        ) : null,
    },
    {
      title: (
        <Checkbox
          checked={allValidChecked}
          onChange={(event) => onToggleAll(event.target.checked)}
        />
      ),
      key: 'checked',
      width: 60,
      render: (_, row) =>
        row.status === 'valid' ? (
          <Checkbox checked={row.checked} onChange={() => onToggleRow(row.cpf)} />
        ) : null,
    },
  ]

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button onClick={onReupload}>Reenviar CSV</Button>
        <Button onClick={onChangeMapping}>Alterar mapeamento</Button>
        <Button
          type="primary"
          disabled={validCheckedCount === 0}
          loading={isImporting}
          onClick={onImport}
        >
          Importar {validCheckedCount > 0 ? `${validCheckedCount} alunos` : ''}
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="cpf"
        size="small"
        pagination={{ pageSize: 20 }}
      />
    </div>
  )
}

function ResultsStep({
  progress,
  isImporting,
  erroGlobal,
  onClose,
}: {
  progress: ImportProgress | null
  isImporting: boolean
  erroGlobal: string | null
  onClose: () => void
}) {
  const columns: ColumnsType<ImportResult> = [
    { title: 'CPF', dataIndex: 'cpf', width: 130 },
    { title: 'Nome', dataIndex: 'nome' },
    {
      title: 'Resultado',
      key: 'resultado',
      width: 200,
      render: (_, result) =>
        result.success ? (
          <Tag color="green">Sucesso</Tag>
        ) : (
          <Space direction="vertical" size={0}>
            <Tag color="red">Erro</Tag>
            {result.error && <span style={{ fontSize: 12, color: 'red' }}>{result.error}</span>}
          </Space>
        ),
    },
  ]

  const resultados = progress?.resultados ?? []
  const total = progress?.total ?? 0
  const processados = progress?.processados ?? 0
  const pct = total > 0 ? Math.round((processados / total) * 100) : 0
  const successCount = resultados.filter((r) => r.success).length

  return (
    <div>
      <Progress
        percent={pct}
        status={isImporting ? 'active' : pct === 100 ? 'success' : 'normal'}
        style={{ marginBottom: 16 }}
      />
      {erroGlobal && (
        <Alert style={{ marginBottom: 16 }} type="error" message={erroGlobal} />
      )}
      {!isImporting && resultados.length > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type={successCount === resultados.length ? 'success' : 'warning'}
          message={`${successCount} de ${resultados.length} alunos importados com sucesso`}
        />
      )}
      <Table
        columns={columns}
        dataSource={resultados}
        rowKey="cpf"
        size="small"
        pagination={false}
      />
      {!isImporting && (
        <Button style={{ marginTop: 16 }} onClick={onClose}>
          Fechar
        </Button>
      )}
    </div>
  )
}
